import { useLayoutEffect } from "react";
import {
  Box,
  LinearProgress,
  alpha,
  useTheme,
} from "@mui/material";

/**
 * Suspense / chunk fallback — one thin header loader strip.
 * Page screens use GenericHeader's bottom bar; do not show a center spinner.
 *
 * Global API-loading registration is optional via callbacks (formerly
 * startApiLoading / endApiLoading from utilities/apiLoadingManager).
 *
 * @param {string} [context]
 * @param {(context: string) => void} [onLoadingStart]
 * @param {() => void} [onLoadingEnd]
 */
const PageLoader = ({
  context = "Loading...",
  onLoadingStart,
  onLoadingEnd,
}) => {
  const theme = useTheme();
  const textColor = theme.palette.primary.contrastText;

  useLayoutEffect(() => {
    onLoadingStart?.(context);
    return () => {
      onLoadingEnd?.();
    };
  }, [context, onLoadingStart, onLoadingEnd]);

  return (
    <Box
      aria-busy="true"
      aria-label={context}
      sx={{
        flex: 1,
        width: "100%",
        minHeight: "100%",
        bgcolor: "background.default",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: (t) => t.zIndex.appBar,
          minHeight: 70,
          height: 70,
          px: 3,
          bgcolor: theme.palette.primary.main,
          color: textColor,
          boxShadow: theme.customShadows?.sm || theme.shadows[2],
          borderBottom: `1px solid ${alpha(theme.palette.common.white, 0.1)}`,
          overflow: "hidden",
          [theme.breakpoints.down("md")]: {
            minHeight: "auto",
            height: "auto",
            py: 1.5,
            px: 2,
          },
        }}
      >
        <LinearProgress
          aria-label={context}
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            height: 3,
            bgcolor: alpha(textColor, 0.14),
            "& .MuiLinearProgress-bar": {
              bgcolor: textColor,
            },
          }}
        />
      </Box>
    </Box>
  );
};

export default PageLoader;
